// /admin/v2/dictionary — Dictionary review (BRIEFS C5.2, SPEC §7): word meanings shown to
// Harsh when he selects a word in the notes. List per chapter (or general words), edit the
// English / Hindi meaning, mark reviewed, and load many entries at once from JSON
// ([{word, chapter_id|null, meaning_en, meaning_hi}] or {chapter_id, entries:[…]}).
// Meanings the AI wrote on the spot are marked "AI" and start as "Not reviewed".
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v2DictList, v2DictSave, v2DictBulk, errText } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';

const hindi = { fontFamily: "'Noto Sans Devanagari', sans-serif" };

function EntryRow({ e, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [en, setEn] = useState(e.meaning_en);
  const [hi, setHi] = useState(e.meaning_hi);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async (body, text) => {
    setBusy(true); setErr('');
    try {
      await v2DictSave(e.id, body);
      setEdit(false);
      onSaved(text);
    } catch (x) {
      setErr(errText(x, 'Could not save.'));
    } finally { setBusy(false); }
  };

  return (
    <tr data-testid={`v2-dict-${e.word}`}>
      <td style={{ verticalAlign: 'top' }}>
        <strong>{e.word}</strong>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {e.chapter_id ? (e.chapter_name || `Chapter ${e.chapter_id}`) : 'All chapters'}
        </div>
      </td>
      <td style={{ verticalAlign: 'top', minWidth: 260 }}>
        {edit ? (
          <textarea className="form-input" rows={2} value={en} onChange={(x) => setEn(x.target.value)}
            aria-label={`English meaning of ${e.word}`} style={{ width: '100%' }} />
        ) : e.meaning_en}
        {edit && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{en.trim().split(/\s+/).filter(Boolean).length} words (aim 20 or fewer)</div>}
      </td>
      <td style={{ verticalAlign: 'top', minWidth: 200, ...hindi }}>
        {edit ? (
          <textarea className="form-input" rows={2} value={hi} onChange={(x) => setHi(x.target.value)}
            aria-label={`Hindi meaning of ${e.word}`} style={{ width: '100%', ...hindi }} />
        ) : e.meaning_hi}
      </td>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        <span className={`badge ${e.origin === 'live' ? 'badge-yellow' : 'badge-blue'}`}>
          {e.origin === 'live' ? 'AI' : 'Prepared'}
        </span>{' '}
        <span className={`badge ${e.is_reviewed ? 'badge-green' : 'badge-red'}`}>
          {e.is_reviewed ? 'Reviewed' : 'Not reviewed'}
        </span>
      </td>
      <td style={{ verticalAlign: 'top' }}>
        {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 4 }}>{err}</div>}
        {edit ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => save({ meaning_en: en, meaning_hi: hi, is_reviewed: true }, `"${e.word}" saved and marked reviewed.`)}>
              Save as reviewed
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy}
              onClick={() => { setEdit(false); setEn(e.meaning_en); setHi(e.meaning_hi); setErr(''); }}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(true)}>Edit</button>
            {e.is_reviewed ? (
              <button className="btn btn-ghost btn-sm" disabled={busy}
                onClick={() => save({ is_reviewed: false }, `"${e.word}" marked not reviewed.`)}>Mark not reviewed</button>
            ) : (
              <button className="btn btn-primary btn-sm" disabled={busy}
                onClick={() => save({ is_reviewed: true }, `"${e.word}" marked reviewed.`)}>Mark reviewed</button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function BulkLoad({ onDone }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const run = async (dryRun) => {
    setBusy(true); setMsg(null); setReport(null);
    let data;
    try {
      data = JSON.parse(raw);
    } catch (x) {
      setBusy(false);
      setMsg({ kind: 'err', text: 'This is not valid JSON. Please check the text.' });
      return;
    }
    try {
      const rep = await v2DictBulk(data, dryRun);
      setReport(rep);
      if (!dryRun && rep.applied) onDone(`Saved: ${rep.added.length} new, ${rep.updated.length} changed.`);
      else if (!dryRun && !rep.errors.length) setMsg({ kind: 'ok', text: 'Nothing to change.' });
    } catch (x) {
      setMsg({ kind: 'err', text: errText(x, 'Could not load the entries.') });
    } finally { setBusy(false); }
  };

  if (!open) {
    return <button className="btn btn-ghost" onClick={() => setOpen(true)}>Load many from JSON…</button>;
  }
  return (
    <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Load many entries</div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>
        Paste a list like <code>[{'{'}"word": "hybrid", "chapter_id": 24, "meaning_en": "…", "meaning_hi": "…"{'}'}]</code>.
        Use <code>"chapter_id": null</code> for a word that means the same in every chapter. Press Check first.
      </p>
      <textarea className="form-input" rows={8} value={raw} onChange={(x) => setRaw(x.target.value)}
        aria-label="Dictionary entries as JSON" style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }} />
      <Msg msg={msg} />
      {report && (
        <div style={{ fontSize: 14, margin: '8px 0' }} data-testid="v2-dict-report">
          {report.errors.length > 0 ? (
            <div style={{ color: 'var(--danger)' }}>
              <strong>{report.errors.length} problem(s) — nothing was saved:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>{report.errors.slice(0, 30).map((t) => <li key={t}>{t}</li>)}</ul>
            </div>
          ) : (
            <div>
              {report.added.length} new · {report.updated.length} changed · {report.unchanged} the same
              {report.applied ? ' — saved.' : ''}
            </div>
          )}
          {report.warnings.length > 0 && (
            <ul style={{ margin: '4px 0 0 18px', color: '#92400E' }}>{report.warnings.slice(0, 30).map((t) => <li key={t}>{t}</li>)}</ul>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-ghost" disabled={busy || !raw.trim()} onClick={() => run(true)}>Check</button>
        <button className="btn btn-primary" disabled={busy || !raw.trim()} onClick={() => run(false)}>Save</button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => { setOpen(false); setReport(null); setMsg(null); }}>Close</button>
      </div>
    </div>
  );
}

export default function AdminV2Dictionary() {
  const [params, setParams] = useSearchParams();
  const chapter = params.get('chapter') || 'all';
  const unreviewed = params.get('unreviewed') === '1';
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(null);
  const query = { chapter, unreviewed: unreviewed ? 1 : 0, q: q.trim() || undefined };
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['v2-dict', chapter, unreviewed, q.trim()],
    queryFn: () => v2DictList(query),
    placeholderData: (prev) => prev,
  });
  const setParam = (k, v) => {
    const next = Object.fromEntries(params.entries());
    if (v) next[k] = v; else delete next[k];
    setParams(next);
  };
  const saved = async (text) => { await refetch(); setMsg({ kind: 'ok', text }); };

  return (
    <div>
      <V2Tabs title="Dictionary"
        intro="Word meanings Harsh sees when he selects a word in the notes. A chapter meaning is used first, then a general one; a word in neither is written on the spot by the AI and saved here as “AI · Not reviewed”." />
      <Msg msg={msg} />
      <div className="card" style={{ marginBottom: 16, padding: '14px 18px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="form-select" value={chapter} style={{ maxWidth: 420 }} aria-label="Chapter"
          onChange={(x) => setParam('chapter', x.target.value === 'all' ? '' : x.target.value)}>
          <option value="all">All entries</option>
          <option value="global">General words (all chapters)</option>
          {(data ? data.chapters : []).filter((c) => c.chapter_id).map((c) => (
            <option key={c.chapter_id} value={String(c.chapter_id)}>{c.name || `Chapter ${c.chapter_id}`} ({c.entries})</option>
          ))}
        </select>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={unreviewed} onChange={(x) => setParam('unreviewed', x.target.checked ? '1' : '')} />
          Only not reviewed
        </label>
        <input className="form-input" placeholder="Find a word" value={q} onChange={(x) => setQ(x.target.value)}
          aria-label="Find a word" style={{ maxWidth: 220 }} />
        <div style={{ flexGrow: 1 }} />
        <BulkLoad onDone={saved} />
      </div>
      {data && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          {data.chapters.map((c) => (
            <span key={String(c.chapter_id)} style={{ marginRight: 14 }}>
              {c.chapter_id ? (c.name || `Chapter ${c.chapter_id}`) : 'General words'}: {c.entries}
              {c.unreviewed ? ` (${c.unreviewed} not reviewed${c.live ? `, ${c.live} AI` : ''})` : ''}
            </span>
          ))}
        </div>
      )}
      {isLoading ? <div className="spinner" /> : isError ? (
        <div className="card" style={{ color: 'var(--danger)' }}>Could not load the dictionary.</div>
      ) : data.entries.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-muted)' }}>No entries here yet.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr><th>Word</th><th>English</th><th>Hindi</th><th>Kind</th><th /></tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <EntryRow key={`${e.id}-${e.updated_at}`} e={e} onSaved={saved} />
              ))}
            </tbody>
          </table>
          {data.total > data.entries.length && (
            <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)' }}>
              Showing {data.entries.length} of {data.total}. Choose a chapter or find a word to see the rest.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
